import { effect, inject, Injectable, signal } from '@angular/core';
import * as turf from '@turf/turf';
import { Subject } from 'rxjs';
import {
  GeoJSONStoreFeatures,
  GeoJSONStoreGeometries,
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawFreehandMode,
  TerraDrawLineStringMode,
  TerraDrawMarkerMode,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { v4 as uuid } from 'uuid';
import { Layer } from '../store/cop/models/layer.model';
import { MapLayerClusterService } from './clustering';
import { MapDrawingStateService } from './map-drawing-state.service';

// constants for TerraDraw mode customization
// TerraDraw requires colors be in hex format
const drawInteraction = 'click-move-or-drag';
const COLOR_PRIMARY_500 = '#7257ff';
const COLOR_PRIMARY_600 = '#4d33cc';
const COLOR_WHITE = '#FFFFFF';
const OUTLINE_WIDTH = 1.5;

// match all entries to registered modes
export enum DrawingMode {
  STATIC = 'static',
  CIRCLE = 'circle',
  FREEHAND = 'freehand',
  MARKER = 'marker',
  POLYGON = 'polygon',
  LINESTRING = 'linestring',
  RECTANGLE = 'rectangle',

  RSD_CIRCLE = 'rsd_circle',
  RSD_FREEHAND = 'rsd_freehand',
  RSD_RADIUS = 'rsd_radius',

  SELECT = 'select',
  EDIT_CIRCLE = 'edit_circle',
  EDIT_MARKER = 'edit_marker',
  EDIT_POLYGON = 'edit_polygon',
  EDIT_LINESTRING = 'edit_linestring',
  EDIT_RECTANGLE = 'edit_rectangle'
}

export interface DrawingEvent {
  mode: DrawingMode;
  geojson: GeoJSONStoreFeatures<GeoJSONStoreGeometries>;
}

function isGeoJSONRectangle(geojson: GeoJSONStoreFeatures<GeoJSONStoreGeometries>): boolean {
  const bbox = turf.bbox(geojson);
  const bboxPoly = turf.bboxPolygon(bbox);
  return turf.booleanEqual(geojson, bboxPoly);
}

function isGeoJSONCircle(geojson: GeoJSONStoreFeatures<GeoJSONStoreGeometries>, tolerance = 0.05): boolean {
  if (!geojson || !geojson.geometry || geojson.geometry.type !== 'Polygon' || !Array.isArray(geojson.geometry.coordinates)) {
    return false;
  }

  // Get the center of the polygon
  const centroid = turf.centroid(geojson);

  // Extract the coordinates (ignoring the closing vertex)
  const coordinates = geojson.geometry.coordinates[0].slice(0, -1);

  let minDistance = Infinity;
  let maxDistance = 0;

  // Measure distance from centroid to each vertex
  coordinates.forEach(coord => {
    const distance = turf.distance(centroid, coord, { units: 'meters' });
    if (distance < minDistance) minDistance = distance;
    if (distance > maxDistance) maxDistance = distance;
  });

  // Calculate the difference ratio
  const variance = (maxDistance - minDistance) / maxDistance;

  // If variance is within the tolerance, it's considered a circle
  return variance <= tolerance;
}

@Injectable({ providedIn: 'root' })
export class DrawingToolsService {
  private draw: TerraDraw; // * NOTE: having multiple instances of this will break styling *
  private mapInstance: google.maps.Map;

  readonly currentMode = signal<DrawingMode>(null);
  readonly drawnShapeType = signal<DrawingMode>(null);

  readonly rsd_currentMode = signal<DrawingMode>(null);
  readonly rsd_drawnShapeType = signal<DrawingMode>(null);

  readonly modes = DrawingMode;

  private mapDrawingStateService = inject(MapDrawingStateService);
  private clusterService = inject(MapLayerClusterService);

  constructor() {
    effect(() => {
      const mode = this.currentMode();
      const rsd_mode = this.rsd_currentMode();

      const activeMode = mode ?? rsd_mode ?? null;

      if (this.isActive && !!activeMode) {
        // set mode as data attribute to allow custom cursors via css
        this.mapInstance?.getDiv()?.querySelector('.terra-draw-google-maps')?.setAttribute('data-mode', activeMode);
      } else {
        this.mapInstance?.getDiv()?.querySelector('.terra-draw-google-maps')?.removeAttribute('data-mode');
      }
    });
  }

  shapeDrawn$ = new Subject<DrawingEvent>();

  init(map: google.maps.Map): void {
    if (!!this.draw) {
      this.teardown();
    }

    this.mapInstance = map;

    this.draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ lib: google.maps, map, coordinatePrecision: 25 }),
      modes: [
        new TerraDrawCircleMode({
          modeName: DrawingMode.RSD_CIRCLE,
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawFreehandMode({
          modeName: DrawingMode.RSD_FREEHAND,
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),

        new TerraDrawCircleMode({
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawFreehandMode({
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawMarkerMode({
          styles: {
            markerUrl: '/assets/markers/drawing_marker.png',
            markerHeight: 44,
            markerWidth: 36
          }
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: COLOR_PRIMARY_600,
            lineStringWidth: OUTLINE_WIDTH,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600
          }
        }),
        new TerraDrawPolygonMode({
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600
          }
        }),
        new TerraDrawRectangleMode({
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawCircleMode({
          modeName: DrawingMode.EDIT_CIRCLE,
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawMarkerMode({
          modeName: DrawingMode.EDIT_MARKER,
          styles: {
            markerUrl: '/assets/markers/drawing_marker.png',
            markerHeight: 44,
            markerWidth: 36
          }
        }),
        new TerraDrawLineStringMode({
          modeName: DrawingMode.EDIT_LINESTRING,
          styles: {
            lineStringColor: COLOR_PRIMARY_600,
            lineStringWidth: OUTLINE_WIDTH,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600
          }
        }),
        new TerraDrawPolygonMode({
          modeName: DrawingMode.EDIT_POLYGON,
          editable: true,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH,
            closingPointColor: COLOR_WHITE,
            closingPointOutlineColor: COLOR_PRIMARY_600
          }
        }),
        new TerraDrawRectangleMode({
          modeName: DrawingMode.EDIT_RECTANGLE,
          drawInteraction,
          styles: {
            fillColor: COLOR_PRIMARY_500,
            outlineColor: COLOR_PRIMARY_600,
            outlineWidth: OUTLINE_WIDTH
          }
        }),
        new TerraDrawSelectMode({
          flags: {
            [DrawingMode.EDIT_MARKER]: {
              feature: {
                draggable: true
              }
            },
            [DrawingMode.EDIT_POLYGON]: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: {
                    draggable: true
                  },
                  draggable: true
                }
              }
            },
            [DrawingMode.EDIT_CIRCLE]: {
              feature: {
                draggable: true,
                coordinates: {
                  draggable: true,
                  resizable: 'center-fixed'
                }
              }
            },
            [DrawingMode.EDIT_RECTANGLE]: {
              feature: {
                draggable: true,
                scaleable: true,
                coordinates: {
                  resizable: 'opposite'
                }
              }
            },
            [DrawingMode.EDIT_LINESTRING]: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: {
                    draggable: true
                  },
                  draggable: true
                }
              }
            }
          },
          styles: {
            selectedPointColor: COLOR_WHITE,
            selectedPointOutlineColor: COLOR_PRIMARY_600,

            selectionPointColor: COLOR_WHITE,
            selectionPointOutlineColor: COLOR_PRIMARY_600,

            selectedPolygonColor: COLOR_PRIMARY_500,
            selectedPolygonOutlineColor: COLOR_PRIMARY_600,

            selectedLineStringColor: COLOR_PRIMARY_600,

            selectedMarkerUrl: '/assets/markers/drawing_marker_selected.png',
            selectedMarkerHeight: 44,
            selectedMarkerWidth: 36,

            midPointColor: COLOR_WHITE,
            midPointOutlineColor: COLOR_PRIMARY_600,
            midPointWidth: 4
          }
        })
      ]
    });
  }

  teardown(): void {
    this.draw?.stop();
    this.draw = null;
    this.mapInstance = null;
    this.currentMode.set(null);
    this.rsd_currentMode.set(null);
    this.drawnShapeType.set(null);
    this.rsd_drawnShapeType.set(null);
    this.setClickForLayerShapesWhileDrawing(true);
  }

  get isActive(): boolean {
    return this.draw?.enabled;
  }

  isSearchMode(mode: DrawingMode): boolean {
    return !this.isRSDMode(mode) && !this.isEditMode(mode) && mode !== DrawingMode.STATIC;
  }

  // used by closest assets in RSD
  isRSDMode(mode: DrawingMode): boolean {
    switch (mode) {
      case DrawingMode.RSD_CIRCLE:
      case DrawingMode.RSD_FREEHAND:
      case DrawingMode.RSD_RADIUS:
        return true;
      default:
        return false;
    }
  }

  isEditMode(mode: DrawingMode): boolean {
    switch (mode) {
      case DrawingMode.EDIT_CIRCLE:
      case DrawingMode.EDIT_MARKER:
      case DrawingMode.EDIT_RECTANGLE:
      case DrawingMode.EDIT_POLYGON:
      case DrawingMode.EDIT_LINESTRING:
      case DrawingMode.SELECT:
        return true;
      default:
        return false;
    }
  }

  private onDraw = (id: string, context: { action: string; mode: string }) => {
    const geojson = this.draw?.getSnapshotFeature(id) ?? null;

    if (!!geojson) {
      this.shapeDrawn$.next({ mode: context.mode as DrawingMode, geojson });
    }

    const shapeType = (geojson?.properties?.mode as DrawingMode) ?? null;

    if (this.isRSDMode(shapeType)) {
      this.rsd_drawnShapeType.set(shapeType);
    } else if (this.isSearchMode(shapeType)) {
      this.drawnShapeType.set(shapeType);
    } else if (this.isEditMode(shapeType)) {
      this.draw.selectFeature(id);
    }
  };

  // should only be called once
  startDrawing(mode: DrawingMode): void {
    if (!this.draw) {
      return;
    }

    if (!this.isActive) {
      this.draw.start();
    }

    this.draw.clear();

    this.setMode(mode);

    // clear existing listener to prevent multiple change emissions
    this.draw.off('finish', this.onDraw);

    this.draw.on('finish', this.onDraw);
  }

  setMode(mode: DrawingMode): void {
    if (!this.isActive) {
      return;
    }

    this.draw.setMode(mode);

    if (this.isRSDMode(mode)) {
      this.rsd_currentMode.set(mode);
      this.currentMode.set(null);
    } else {
      this.currentMode.set(mode);
      this.rsd_currentMode.set(null);
    }

    this.setClickForLayerShapesWhileDrawing(mode === DrawingMode.STATIC);
  }

  setClickForLayerShapesWhileDrawing(clickEnabled: boolean) {
    if (!!!this.mapDrawingStateService.mapState?.layers) {
      return;
    }

    this.clusterService.setClickable(clickEnabled);

    for (let layer of this.mapDrawingStateService.mapState?.layers) {
      this.processDatasetLayerClickChanges(layer, clickEnabled);
    }
  }

  private processDatasetLayerClickChanges(layer: Layer, clickable: boolean): void {
    const layerFeatures = this.mapDrawingStateService.mapFeatures[layer.uId];

    if (!layerFeatures) {
      return;
    }

    Object.values(layerFeatures).forEach(docFeatures => {
      if (!docFeatures) {
        return;
      }

      for (let circle of docFeatures.circles) {
        circle.setOptions({ clickable });
      }

      for (let marker of docFeatures.markers) {
        marker.setOptions({ clickable });
      }

      for (let polygon of docFeatures.polygons) {
        polygon.setOptions({ clickable });
      }

      for (let polyline of docFeatures.polylines) {
        polyline.setOptions({ clickable });
      }

      for (let rectangle of docFeatures.rectangles) {
        rectangle.setOptions({ clickable });
      }
    });
  }

  stopDrawingForSearch(): void {
    if (!this.isActive) {
      return;
    }

    this.draw.setMode('static');
    this.currentMode.set(null);
    this.clearShapesForSearch();
    this.setClickForLayerShapesWhileDrawing(true);
  }

  stopDrawingForRSD(clearExisting: boolean = true): void {
    if (!this.isActive) {
      return;
    }

    this.draw.setMode('static');
    this.rsd_currentMode.set(null);
    if (clearExisting) {
      this.clearShapesForRSD();
    }
    this.setClickForLayerShapesWhileDrawing(true);
  }

  stopDrawingForEdit(): void {
    if (!this.isActive) {
      return;
    }

    this.draw.setMode('static');
    this.currentMode.set(null);
    this.clearShapesForEdit();
    this.setClickForLayerShapesWhileDrawing(true);
  }

  addFeaturesForEdit(features: GeoJSONStoreFeatures<GeoJSONStoreGeometries>[]): string[] {
    if (!this.isActive) {
      return;
    }

    this.clearShapesForSearch();
    // * NOTE * - don't clear currentMode here since it is shared for searching and editing
    this.clearShapesForRSD();
    this.rsd_currentMode.set(null);

    const updatedFeatures = features.map(f => {
      const { geometry, properties } = f;
      const updatedFeature = structuredClone(f);

      // existing geojson functionality uses ascending numbers as id system where TerraDraw relies on UIds.
      // store application id as editingId in properties and override id with UId.
      // set mode property to a configured value for editing functionality
      switch (geometry?.type) {
        case 'Point':
          updatedFeature.properties = {
            ...properties,
            mode: DrawingMode.EDIT_MARKER,
            marker: true,
            editingId: f.id
          };
          break;
        case 'Polygon':
          let mode = DrawingMode.EDIT_POLYGON;

          if (isGeoJSONRectangle(f)) {
            mode = DrawingMode.EDIT_RECTANGLE;
          } else if (isGeoJSONCircle(f)) {
            mode = DrawingMode.EDIT_CIRCLE;
          }

          updatedFeature.properties = {
            ...properties,
            mode,
            editingId: f.id
          };

          break;
        case 'LineString':
          updatedFeature.properties = {
            ...properties,
            mode: DrawingMode.EDIT_LINESTRING,
            editingId: f.id
          };
          break;
        default:
          break;
      }

      updatedFeature.id = uuid();

      return updatedFeature;
    });

    return this.draw
      .addFeatures(updatedFeatures)
      .filter(f => f.valid)
      .map(({ id }) => id.toString());
  }

  getAllDrawnShapes(): GeoJSONStoreFeatures<GeoJSONStoreGeometries>[] {
    if (!this.isActive) {
      return [];
    }

    return this.draw.getSnapshot() ?? [];
  }

  getShapesForSearch(): GeoJSONStoreFeatures<GeoJSONStoreGeometries>[] {
    return this.getAllDrawnShapes().filter(({ properties }) => this.isSearchMode(properties.mode as DrawingMode));
  }

  getShapesForEdit(): GeoJSONStoreFeatures<GeoJSONStoreGeometries>[] {
    // when using select mode, the points that are used to edit shapes are included in feature snapshot
    return this.getAllDrawnShapes().filter(feature => {
      const { mode = null, selectionPoint = null, midPoint = null } = feature?.properties ?? {};

      return this.isEditMode(mode as DrawingMode) && !selectionPoint && !midPoint;
    });
  }

  getShapesForRSD(): GeoJSONStoreFeatures<GeoJSONStoreGeometries>[] {
    return this.getAllDrawnShapes().filter(({ properties }) => this.isRSDMode(properties.mode as DrawingMode));
  }

  clearShapesForSearch(): void {
    if (!this.isActive) {
      return;
    }

    const shapeIds = this.getShapesForSearch().map(f => f.id);
    this.draw.removeFeatures(shapeIds);
    this.drawnShapeType.set(null);
  }

  clearShapesForRSD(): void {
    if (!this.isActive) {
      return;
    }

    const shapeIds = this.getShapesForRSD().map(f => f.id);
    this.draw.removeFeatures(shapeIds);
    this.rsd_drawnShapeType.set(null);
  }

  clearShapesForEdit(): void {
    if (!this.isActive) {
      return;
    }

    const shapeIds = this.getShapesForEdit().map(f => f.id);
    this.draw.removeFeatures(shapeIds);
  }

  removeShapes(ids: string[]): void {
    if (!this.isActive) {
      return;
    }

    this.draw.removeFeatures(ids);
  }

  selectShape(id: string): void {
    if (!this.isActive) {
      return;
    }

    this.draw.selectFeature(id);
  }
}
